import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sunrise, Send, Image as ImageIcon } from "lucide-react";

const Btn = ({ children, className = "", ...rest }) => (
  <button
    className={`inline-flex items-center gap-2 rounded-sm px-4 py-2 font-oswald text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-60 ${className}`}
    {...rest}
  >
    {children}
  </button>
);

export function ImpactPostCard() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState("");

  const load = () => api.get("/impact/post-config").then((r) => setCfg(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    try {
      const { data } = await api.put("/impact/post-config", { enabled: !cfg.enabled });
      setCfg(data);
      toast.success(data.enabled ? "Daily impact post enabled — 6:00 AM ET" : "Daily impact post paused");
    } catch { toast.error("Save failed"); }
  };

  const postNow = async () => {
    setBusy("post");
    try {
      const { data } = await api.post("/impact/post-now");
      toast.success(`Facebook: ${data.facebook} · X: ${data.twitter}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Post failed"); } finally { setBusy(""); }
  };

  const previewCard = async () => {
    setBusy("preview");
    try {
      const { data } = await api.post("/impact/preview-card");
      setPreview(data.url + "?ts=" + Date.now());
    } catch { toast.error("Preview failed"); } finally { setBusy(""); }
  };

  if (!cfg) return null;
  const lr = cfg.last_result;
  return (
    <div className="glass rounded-sm p-5 space-y-3" data-testid="impact-post-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-oswald text-sm font-bold uppercase tracking-widest text-white">
            <Sunrise className="h-4 w-4 text-[#ffd60a]" /> Daily Impact Post · 6:00 AM ET
          </div>
          <p className="text-xs text-gray-400">
            Every morning the Georgia Weather Impact card (Travel, Schools, Heat, Severe…) is rendered as a 1080×1350 graphic and posted to Facebook and X. Idempotent per day.
          </p>
        </div>
        <button
          type="button"
          data-testid="impact-post-toggle"
          onClick={toggle}
          className={`rounded-full px-4 py-1.5 font-oswald text-xs font-bold uppercase tracking-widest ${cfg.enabled ? "bg-green-500/20 text-green-300" : "bg-white/10 text-gray-300"}`}
        >
          {cfg.enabled ? "Scheduled · On" : "Paused"}
        </button>
      </div>
      <div className="flex flex-wrap gap-3">
        <Btn data-testid="impact-post-now-btn" onClick={postNow} disabled={busy === "post"} className="bg-[#1f63c9] text-white hover:bg-[#2a74d8]">
          <Send className="h-4 w-4" /> {busy === "post" ? "Posting…" : "Post Now"}
        </Btn>
        <Btn data-testid="impact-preview-btn" onClick={previewCard} disabled={busy === "preview"} className="border border-white/15 text-white hover:border-[#1f63c9]">
          <ImageIcon className="h-4 w-4" /> {busy === "preview" ? "Rendering…" : "Preview Card"}
        </Btn>
      </div>
      {lr && (
        <div className="rounded-sm border border-white/10 bg-black/40 p-3 font-mono text-[11px] text-gray-300" data-testid="impact-post-last">
          <div>Last run: {cfg.last_run ? new Date(cfg.last_run).toLocaleString("en-US", { timeZone: "America/New_York" }) + " ET" : "—"}</div>
          <div className={lr.facebook?.startsWith("posted") ? "text-green-300" : lr.facebook?.startsWith("failed") ? "text-red-300" : ""}>Facebook: {lr.facebook}</div>
          <div className={lr.twitter?.startsWith("posted") ? "text-green-300" : lr.twitter?.startsWith("failed") ? "text-red-300" : ""}>X: {lr.twitter}</div>
        </div>
      )}
      {preview && <img src={preview} alt="Impact card preview" className="w-full max-w-sm rounded-sm border border-white/15" data-testid="impact-preview-img" />}
    </div>
  );
}
